import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Cropper from 'react-easy-crop';
import { Area, MediaSize } from 'react-easy-crop/types';
import { Typography } from '@mui/material';
import getCroppedImg from './utils';
import StyledSlider from '../Sliders/StyledSlider';

import styles from './styles.module.scss';
import ButtonWithArrowIcon from 'components/Buttons/ButtonWithArrowIcon';

type Crop = {
  x: number;
  y: number;
};

const defaultCrop = {
  x: 0,
  y: 0,
};

type InputProps = {
  fileUrl: string;
  aspectRatio?: number;
  onSave: (file: Blob) => void;
};

type Config = {
  aspect: number;
  cropShape: 'rect' | 'round';
};

const config: Config = {
  aspect: 1,
  cropShape: 'rect',
};

const isTouchDevice = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

export const CropImage = ({ fileUrl, onSave, ...props }: InputProps) => {
  const [crop, setCrop] = useState<Crop>(defaultCrop);
  const [zoom, setZoom] = useState<number>(1);
  const [rotation, setRotation] = useState<number>(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [mediaSize, setMediaSize] = useState<MediaSize | null>(null);

  const containerRef = useRef(null);
  const [currentWidth, setCurrentWidth] = useState(0);

  const aspectRatioHeight = useMemo(() => {
    return currentWidth / (props.aspectRatio || config.aspect);
  }, [currentWidth, props.aspectRatio]);

  const objectFit = useMemo(() => {
    return (mediaSize?.height || 0) > (mediaSize?.width || 0)
      ? 'horizontal-cover'
      : 'vertical-cover';
  }, [mediaSize]);

  const hideZoom = useMemo(() => isTouchDevice(), []);

  useLayoutEffect(() => {
    setCurrentWidth((containerRef?.current as any)?.offsetWidth);
  }, []);

  const onCropChange = (crop: Crop) => {
    setCrop(crop);
  };

  const onZoomChange = (zoom: number) => {
    setZoom(zoom);
  };

  const onCropComplete = useCallback(
    (croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  const showCroppedImage = useCallback(async () => {
    try {
      if (croppedAreaPixels) {
        const croppedImage = await getCroppedImg(
          fileUrl,
          croppedAreaPixels,
          rotation
        );
        if (croppedImage) {
          onSave(croppedImage);
        } else {
          // TODO
        }
      }
    } catch (e) {
      console.error(e);
    }
  }, [croppedAreaPixels, rotation]);

  const onImageLoaded = (mediaSize: MediaSize) => {
    setMediaSize(mediaSize);

    setTimeout(() => {
      setCurrentWidth((containerRef?.current as any)?.offsetWidth);
    }, 500);
  };

  return (
    <div className={styles.root}>
      <div
        className={styles.cropContainer}
        ref={containerRef}
        style={{
          height: `calc(${aspectRatioHeight}px + 0px)`,
        }}
      >
        <Cropper
          key={objectFit}
          image={fileUrl}
          crop={crop}
          zoom={zoom}
          rotation={rotation}
          restrictPosition={false}
          objectFit={objectFit}
          aspect={props.aspectRatio || config.aspect}
          cropShape={config.cropShape}
          showGrid={false}
          onCropChange={onCropChange}
          onCropComplete={onCropComplete}
          onZoomChange={onZoomChange}
          onRotationChange={setRotation}
          onMediaLoaded={onImageLoaded}
          style={{
            cropAreaStyle: {
              borderRadius: '10px',
            },
          }}
        />
      </div>

      <div className={styles.controls}>
        {!hideZoom && (
          <>
            {' '}
            <div className={styles.sliderContainer}>
              <Typography
                variant="overline"
                classes={{ root: styles.sliderLabel }}
              >
                Zoom
              </Typography>
              <StyledSlider
                value={zoom}
                min={0}
                max={3}
                step={0.01}
                aria-labelledby="Zoom"
                classes={{ root: styles.slider }}
                onChange={(e, zoom) => setZoom(Number(zoom))}
              />
            </div>
            <div className={styles.sliderContainer}>
              <Typography
                variant="overline"
                classes={{ root: styles.sliderLabel }}
              >
                Rotation
              </Typography>
              <StyledSlider
                value={rotation}
                min={0}
                max={360}
                step={1}
                aria-labelledby="Rotation"
                classes={{ root: styles.slider }}
                onChange={(e, rotation) => setRotation(Number(rotation))}
              />
            </div>
          </>
        )}
        <div className={styles.actionsPanel}>
          <ButtonWithArrowIcon
            variant="contained"
            color="primary"
            onClick={showCroppedImage}
          >
            Confirm
          </ButtonWithArrowIcon>
        </div>
      </div>
    </div>
  );
};
